import { AutocompleteWithLabel, SelectWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery
} from '@apollo/client'
import { Button, Grow, MenuItem } from '@material-ui/core'
import { useState, useEffect, FC } from 'react'
import { toast } from 'react-toastify'

import { ENTITY_LIST_QUERY } from '../../graphql/entityList'
import { getCurrenciesAvailable } from '../../helpers/userData'
import { getTranslate, lang } from '../../localization'
import { EntityList } from '../../types/entityList'
import { EntityType } from '../../types/reserveMonitoringList'
import { WalletFilters, WalletListItem, WalletType } from '../../types/wallet'
import useStyles from './styles/FilterBoxDesktop.styles'

type FilterBoxDesktopProps = {
  filters: WalletFilters
  setfilters: (v: WalletFilters) => void
  setPage: (v: number) => void
  getWallets: LazyQueryExecFunction<
    {
      wallets: WalletListItem[]
    },
    OperationVariables
  >
  walletTypes: WalletType[]
  loadingForWallets: boolean
  loadingForWalletTypes: boolean
  walletTypesError: boolean
  retryWalletTypes: () => void
  isRoleAdmin: boolean
}

export const FilterBoxDesktop: FC<FilterBoxDesktopProps> = ({
  filters,
  setfilters,
  getWallets,
  loadingForWallets,
  setPage,
  walletTypes,
  loadingForWalletTypes,
  walletTypesError,
  retryWalletTypes
}) => {
  const classes = useStyles()

  const [entityOptions, setEntityOptions] = useState<EntityList[]>([])
  const [autoCompleteLabelForEntity, setautoCompleteLabelForEntity] =
    useState<any>(null)
  const [autoCompleteLabelForWalletType, setautoCompleteLabelForWalletType] =
    useState<any[]>([])
  const [autoCompleteLabelForCurrency, setautoCompleteLabelForCurrency] =
    useState<any>(null)
  const [creditorEntityOptions, setCreditorEntityOptions] = useState<
    EntityList[]
  >([])
  const [
    autoCompleteLabelForCreditorEntity,
    setautoCompleteLabelForCreditorEntity
  ] = useState<any>(null)

  const [getEntityList, { loading: loadingForEntityList }] = useLazyQuery<{
    entityList: EntityList[]
  }>(ENTITY_LIST_QUERY, {
    onCompleted: (res) => {
      setEntityOptions(res.entityList)
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const [getCreditorEntityList, { loading: loadingForCreditorEntityList }] =
    useLazyQuery<{
      entityList: EntityList[]
    }>(ENTITY_LIST_QUERY, {
      onCompleted: (res) => {
        setCreditorEntityOptions(res.entityList)
      },
      onError: (err) => {
        toast.error(err.message)
      }
    })

  // For normal entity
  useEffect(() => {
    if (filters.entity && filters.entity?.entityType) {
      getEntityList({
        variables: {
          entityType: filters.entity.entityType
        }
      })
    }
  }, [filters.entity?.entityType])

  // For creditor entity
  useEffect(() => {
    if (filters.creditorEntity && filters.creditorEntity?.entityType) {
      getCreditorEntityList({
        variables: {
          entityType: filters.creditorEntity.entityType
        }
      })
    }
  }, [filters.creditorEntity?.entityType])

  return (
    <Grow in>
      <div className={classes.root}>
        <div className={classes.filterContainer}>
          {
            <>
              <SelectWithLabel
                label={getTranslate('Entity Type')}
                fullWidth
                value={
                  filters.entity?.entityType ? filters.entity?.entityType : ''
                }
                onChange={(event) => {
                  const newEntityType = event.target.value as EntityType
                  setfilters({
                    ...filters,
                    entity: {
                      entityType: newEntityType,
                      entityID: '',
                      entityCode: undefined
                    }
                  })
                  setautoCompleteLabelForEntity(null)
                }}
                size="small">
                <MenuItem
                  key="Channel"
                  value="Channel"
                  classes={{
                    root: lang === 'fa' ? classes.menuItem : undefined
                  }}>
                  {getTranslate('Channel')}
                </MenuItem>
                <MenuItem
                  key="Provider"
                  value="Provider"
                  classes={{
                    root: lang === 'fa' ? classes.menuItem : undefined
                  }}>
                  {getTranslate('Provider')}
                </MenuItem>
              </SelectWithLabel>
              <AutocompleteWithLabel
                options={loadingForEntityList ? [] : entityOptions}
                inputSize="small"
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) =>
                  option.label
                    .toLowerCase()
                    .includes(value.label.toLowerCase()) === true
                }
                value={autoCompleteLabelForEntity}
                onChange={(_, value: any) => {
                  if (value && filters.entity) {
                    setautoCompleteLabelForEntity({
                      value: value.value,
                      label: value.label,
                      id: value.id
                    })
                    setfilters({
                      ...filters,
                      entity: {
                        ...filters.entity,
                        entityID: value.id,
                        entityCode: value.entityCode
                      }
                    })
                  } else {
                    setautoCompleteLabelForEntity(null)
                    setfilters({
                      ...filters,
                      entity: {
                        ...filters.entity!,
                        entityID: '',
                        entityCode: undefined
                      }
                    })
                  }
                }}
                label={getTranslate('Entity')}
                loading={loadingForEntityList}
                loadingText={getTranslate('Loading') + '...'}
              />
            </>
          }
          <AutocompleteWithLabel
            label={getTranslate('Wallet type')}
            options={walletTypes.map((type) => ({
              label: getTranslate(type.walletType),
              value: type.walletType,
              id: type.walletType
            }))}
            inputSize="small"
            getOptionLabel={(option) => option.label}
            getOptionSelected={(option, value) =>
              option.label.toLowerCase().includes(value.label.toLowerCase()) ===
              true
            }
            multiple
            value={autoCompleteLabelForWalletType}
            onChange={(_, v: any) => {
              if (v.length > 0) {
                const temp = v.map((item: any) => ({
                  label: item.label,
                  value: item.value,
                  id: item.id
                }))
                setautoCompleteLabelForWalletType(temp)
                setfilters({
                  ...filters,
                  walletTypes: v.map((walletType: any) => walletType.value)
                })
              } else {
                setautoCompleteLabelForWalletType([])
                setfilters({
                  ...filters,
                  walletTypes: undefined
                })
              }
            }}
            size="small"
            loading={loadingForWalletTypes}
            loadingText={getTranslate('Loading') + '...'}
            error={walletTypesError}
            helpertext={
              walletTypesError
                ? getTranslate('Unable to load wallet types. Please retry.')
                : undefined
            }
            noOptionsText={
              walletTypesError
                ? getTranslate('Unable to load wallet types. Please retry.')
                : getTranslate('No wallet types are configured.')
            }
            endIcon={
              walletTypesError ? (
                <Button
                  color="primary"
                  onClick={(event) => {
                    event.stopPropagation()
                    retryWalletTypes()
                  }}>
                  {getTranslate('Retry')}
                </Button>
              ) : undefined
            }
          />
          <SelectWithLabel
            label={getTranslate('Activation')}
            fullWidth
            value={
              filters.active !== undefined
                ? filters.active
                  ? 'Active'
                  : 'Deactive'
                : ''
            }
            onChange={(event) => {
              setfilters({
                ...filters,
                active: event.target.value === 'Active'
              })
            }}
            size="small">
            <MenuItem
              key="Active"
              value="Active"
              classes={{
                root: lang === 'fa' ? classes.menuItem : undefined
              }}>
              {getTranslate('Active')}
            </MenuItem>
            <MenuItem
              key="Deactive"
              value="Deactive"
              classes={{
                root: lang === 'fa' ? classes.menuItem : undefined
              }}>
              {getTranslate('Deactive')}
            </MenuItem>
          </SelectWithLabel>
          <AutocompleteWithLabel
            data-cy="AccommodationAccountsCurrencyFilter"
            fullWidth
            value={autoCompleteLabelForCurrency}
            onChange={(_, v: any) => {
              if (v) {
                setautoCompleteLabelForCurrency({
                  value: v.value,
                  label: v.label
                })
                setfilters({
                  ...filters,
                  currency: v.value
                })
              } else {
                setautoCompleteLabelForCurrency(null)
                setfilters({
                  ...filters,
                  currency: undefined
                })
              }
            }}
            inputSize="small"
            label={getTranslate('Currency')}
            options={getCurrenciesAvailable().map((currency) => ({
              label: currency as string,
              value: currency as string
            }))}
          />
          {filters.walletTypes?.includes('ProviderCredit') ? (
            <>
              <SelectWithLabel
                label={getTranslate('Creditor Entity Type')}
                fullWidth
                value={
                  filters.creditorEntity?.entityType
                    ? filters.creditorEntity?.entityType
                    : ''
                }
                onChange={(event) => {
                  const newEntityType = event.target.value as EntityType
                  setfilters({
                    ...filters,
                    creditorEntity: {
                      entityType: newEntityType,
                      entityID: '',
                      entityCode: undefined
                    }
                  })
                  setautoCompleteLabelForCreditorEntity(null)
                }}
                size="small">
                <MenuItem
                  key="Channel"
                  value="Channel"
                  classes={{
                    root: lang === 'fa' ? classes.menuItem : undefined
                  }}>
                  {getTranslate('Channel')}
                </MenuItem>
                <MenuItem
                  key="Provider"
                  value="Provider"
                  classes={{
                    root: lang === 'fa' ? classes.menuItem : undefined
                  }}>
                  {getTranslate('Provider')}
                </MenuItem>
              </SelectWithLabel>
              <AutocompleteWithLabel
                options={
                  loadingForCreditorEntityList ? [] : creditorEntityOptions
                }
                inputSize="small"
                getOptionLabel={(option) => option.label}
                getOptionSelected={(option, value) =>
                  option.label
                    .toLowerCase()
                    .includes(value.label.toLowerCase()) === true
                }
                value={autoCompleteLabelForCreditorEntity}
                onChange={(_, value: any) => {
                  if (value && filters.creditorEntity) {
                    setautoCompleteLabelForCreditorEntity({
                      value: value.value,
                      label: value.label,
                      id: value.id
                    })
                    setfilters({
                      ...filters,
                      creditorEntity: {
                        ...filters.creditorEntity,
                        entityID: value.id,
                        entityCode: value.code
                      }
                    })
                  } else {
                    setautoCompleteLabelForCreditorEntity(null)
                    setfilters({
                      ...filters,
                      creditorEntity: {
                        ...filters.creditorEntity!,
                        entityID: '',
                        entityCode: undefined
                      }
                    })
                  }
                }}
                label={getTranslate('Creditor Entity')}
                loading={loadingForCreditorEntityList}
                loadingText={getTranslate('Loading') + '...'}
              />
            </>
          ) : (
            <>
              <div />
              <div />
            </>
          )}
          <div className={classes.ButtonsContainer}>
            <Button
              className={classes.resetButton}
              variant="text"
              color="secondary"
              onClick={() => {
                setautoCompleteLabelForCurrency(null)
                setautoCompleteLabelForWalletType([])
                setautoCompleteLabelForEntity(null)
                setautoCompleteLabelForCreditorEntity(null)
                setfilters({
                  ...filters,
                  entity: undefined,
                  walletTypes: undefined,
                  active: undefined,
                  currency: undefined,
                  creditorEntity: undefined
                })
              }}>
              {getTranslate('Reset')}
            </Button>
            <Button
              className={classes.applyButton}
              variant="contained"
              color="primary"
              disabled={loadingForWallets}
              onClick={() => {
                getWallets({
                  variables: {
                    filters: filters
                  }
                })
                setPage(1)
              }}>
              {getTranslate('Apply')}
            </Button>
          </div>
        </div>
      </div>
    </Grow>
  )
}
